
import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface ExternalAccountLink {
  id: string;
  user_id: string;
  external_project_id: string;
  external_user_email: string;
  external_user_id: string | null;
  is_verified: boolean;
  last_sync_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ExternalRoundStats {
  id: string;
  user_id: string;
  external_round_id: string;
  round_date: string;
  course_name: string | null;
  course_location: string | null;
  holes_played: number;
  total_score: number | null;
  score_vs_par: number | null;
  total_putts: number | null;
  putts_per_hole: number | null;
  fairways_hit: number;
  fairways_total: number;
  fairway_percentage: number | null;
  greens_in_regulation: number;
  greens_total: number;
  gir_percentage: number | null;
  scrambling_opportunities: number | null;
  scrambling_made: number | null;
  scrambling_percentage: number | null;
  fairway_tendencies: {
    left: number;
    center: number;
    right: number;
  } | null;
  hole_details: unknown;
  notes: string | null;
  synced_at: string;
}

export interface CourseStatsFilters {
  holesFilter: "all" | "9" | "18";
  courseFilter: string;
  dateFrom: Date | null;
  dateTo: Date | null;
  showAllRounds: boolean;
}

// Get default date filters (current year)
const getDefaultFilters = (): CourseStatsFilters => {
  const currentYear = new Date().getFullYear();
  return {
    holesFilter: "18",
    courseFilter: "all",
    dateFrom: new Date(currentYear, 0, 1), // January 1st of current year
    dateTo: new Date(), // Today
    showAllRounds: false,
  };
};

export const useCourseStats = () => {
  const { toast } = useToast();
  const [stats, setStats] = useState<ExternalRoundStats[]>([]);
  const [accountLink, setAccountLink] = useState<ExternalAccountLink | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAccountLinkLoading, setIsAccountLinkLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [filters, setFilters] = useState<CourseStatsFilters>(getDefaultFilters());

  // Fetch account link
  const fetchAccountLink = async () => {
    try {
      setIsAccountLinkLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("external_account_links")
        .select("*")
        .eq("user_id", user.id)
        .single();

      if (error && error.code !== "PGRST116") {
        console.error("Error fetching account link:", error);
      }

      setAccountLink(data as ExternalAccountLink | null);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsAccountLinkLoading(false);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("external_round_stats")
        .select("*")
        .eq("user_id", user.id)
        .order("round_date", { ascending: false });

      if (error) {
        console.error("Error fetching stats:", error);
        return;
      }

      setStats((data as ExternalRoundStats[]) || []);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Sync rounds from external project
  const syncRounds = async () => {
    try {
      setIsSyncing(true);
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error("Not authenticated");
      }

      const { data, error } = await supabase.functions.invoke("sync-external-rounds", {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        throw error;
      }

      if (!data.success) {
        throw new Error(data.error || "Sync failed");
      }

      // Refresh data after sync
      await fetchStats();
      await fetchAccountLink();

      return data;
    } catch (error) {
      console.error("Sync error:", error);
      throw error;
    } finally {
      setIsSyncing(false);
    }
  };

  // Get unique courses for filter - filtered by holes selection
  const courses = useMemo(() => {
    const uniqueCourses = new Set<string>();
    stats.forEach(s => {
      if (s.course_name) {
        // Only include courses that match the holes filter
        if (filters.holesFilter === "all" || s.holes_played === parseInt(filters.holesFilter)) {
          uniqueCourses.add(s.course_name);
        }
      }
    });
    return Array.from(uniqueCourses).sort();
  }, [stats, filters.holesFilter]);

  // Apply filters
  const filteredStats = useMemo(() => {
    return stats.filter(stat => {
      // Holes filter
      if (filters.holesFilter !== "all") {
        const holesNum = parseInt(filters.holesFilter);
        if (stat.holes_played !== holesNum) return false;
      }

      // Course filter
      if (filters.courseFilter !== "all" && stat.course_name !== filters.courseFilter) {
        return false;
      }

      // Date filters (only apply if not showing all rounds)
      if (!filters.showAllRounds) {
        const roundDate = new Date(stat.round_date);
        if (filters.dateFrom && roundDate < filters.dateFrom) return false;
        if (filters.dateTo && roundDate > filters.dateTo) return false;
      }

      return true;
    });
  }, [stats, filters]);

  useEffect(() => {
    fetchAccountLink();
    fetchStats();
  }, []);

  const refetch = () => {
    fetchAccountLink();
    fetchStats();
  };

  return {
    stats,
    filteredStats,
    isLoading,
    accountLink,
    isAccountLinkLoading,
    filters,
    setFilters,
    courses,
    syncRounds,
    isSyncing,
    refetch,
  };
};
